import { type FC, memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { Tournament } from '@/api/cms';
import { ImageComponent } from '@/components/Image';
import { CountdownWidget } from '@/modules/common/components/Countdown/CountdownWidget';
import { getStatusByTimeRange, ScheduleStatus } from '@/modules/common/helpers';
import { useGetCommonStore } from '@/store';
import { blurImagePlaceholder } from '@/theme/placeholders';
import { useTournament } from '../../hooks';
import { TournamentActionButton } from './components/TournamentActionButton';
import { TournamentBadge } from './components/TournamentBadge';
import { TournamentLimits } from './components/TournamentLimits';
import { badgeGradientMap } from './helpers';
import { ScTournamentCard } from './styled';

export interface ITournamentCard extends Tournament {
  preview?: boolean;
}

export const TournamentCard: FC<ITournamentCard> = memo(
  ({
    name,
    banner: {
      prizePool,
      backgroundDesktop,
      mainImg,
      durationFrom,
      durationTo,
      minBet,
      minSpins,
      badge,
      inHouse,
    },
    preview,
    mechanicsDescription,
  }) => {
    const { t } = useTranslation(['common', 'tournaments']);
    const useCommonStore = useGetCommonStore();

    const isAuthenticated = useCommonStore.use.isAuthenticated();
    const { isDailyCashDropsTournament } = useTournament(name);

    const [timeLeft, mode] = useMemo(
      () => getStatusByTimeRange(durationFrom, durationTo),
      [durationFrom, durationTo]
    );
    const badgeGradientClass = useMemo(
      () => (badge ? badgeGradientMap[badge] : ''),
      [badge]
    );
    const finishedClass = useMemo(
      () => (mode === ScheduleStatus.FINISHED ? 'finished' : ''),
      [mode]
    );
    const upcomingClass = useMemo(
      () => (mode === ScheduleStatus.COMING_SOON ? 'upcoming' : ''),
      [mode]
    );
    const withoutHoverClass = useMemo(
      () => (preview ? '' : 'without-hover'),
      [preview]
    );

    const labelTextMap = {
      [ScheduleStatus.ACTIVE]: 'common:tournaments.endsIn',
      [ScheduleStatus.COMING_SOON]: 'common:tournaments.startsIn',
      [ScheduleStatus.FINISHED]: 'common:finished',
    };

    return (
      <ScTournamentCard
        className={`${finishedClass} ${badgeGradientClass} ${withoutHoverClass} ${upcomingClass}`}
      >
        <div className="tournament-card-wrapper">
          {inHouse && (
            <div className="in-house-badge">
              <span>{t('tournaments:inHouse')}</span>
            </div>
          )}
          {badge && <TournamentBadge type={badge} />}
          {!isDailyCashDropsTournament && (
            <CountdownWidget
              accent={mode === ScheduleStatus.COMING_SOON}
              label={t(labelTextMap[mode])}
              showCountdown={mode !== ScheduleStatus.FINISHED}
              timeLeft={Math.floor(timeLeft / 1e3)}
            />
          )}

          <div className="banner">
            <ImageComponent
              alt="tournament"
              blurDataURL={blurImagePlaceholder}
              fill
              placeholder="blur"
              priority
              src={mainImg?.url || backgroundDesktop?.url || ''}
              style={{ objectPosition: `${mainImg ? 'center' : '65%'} center` }}
            />
            <div className="bottom-gradient" />
          </div>

          <div className="card-footer">
            <div>
              <div className="name">
                <p title={name}>{name}</p>
              </div>

              {prizePool && <div className="prize">{prizePool}</div>}
            </div>

            <div className="cta-box">
              <TournamentActionButton
                isAuthenticated={isAuthenticated}
                mode={mode}
                name={name}
                preview={preview}
              />
            </div>

            <TournamentLimits minBet={minBet} minSpins={minSpins} name={name} />
          </div>
        </div>
        {mechanicsDescription && !preview && (
          <div className="card-additional-description">
            <ImageComponent
              alt="new mechanics icon"
              className="description-icon"
              height={64}
              src="/tournaments/tournament-card/new-mechanics-badge-large.webp"
              width={59}
            />
            <span
              className="description-text"
              dangerouslySetInnerHTML={{ __html: mechanicsDescription }}
            />
          </div>
        )}
      </ScTournamentCard>
    );
  }
);
